import React, { useEffect, useRef, useState } from 'react';
import { CAMERA_FORMATS, CAMERA_VIDEO, cameraErrorCode } from './cameraScan';

const SLOW_READ_MS = 12000;
const SCAN_INTERVAL_MS = 120;

// Full-screen camera view. Reports the first barcode it reads, then stops the camera.
// It only fills the search box; nothing is moved from here.
// Decoding uses zxing-wasm (the C++ ZXing reader): the older JavaScript port missed
// thin Code 128 product barcodes, such as DFL-MLUX's, that this one reads.
const CameraScanSheet = ({ onRead, onError, onClose, formats = CAMERA_FORMATS }) => {
  const videoRef = useRef(null);
  const handlers = useRef({ onRead, onError });
  handlers.current = { onRead, onError };
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    let stream = null;
    let scanTimer = null;
    let finished = false;
    const slowTimer = setTimeout(() => setSlow(true), SLOW_READ_MS);
    const finish = () => {
      finished = true;
      clearTimeout(slowTimer);
      clearTimeout(scanTimer);
      if (stream) stream.getTracks().forEach((track) => track.stop());
    };
    const fail = (err) => {
      if (finished) return;
      finish();
      handlers.current.onError(cameraErrorCode(err));
    };
    (async () => {
      try {
        const [{ prepareZXingModule, readBarcodes }, { default: wasmUrl }] = await Promise.all([
          import('zxing-wasm/reader'),
          import('zxing-wasm/reader/zxing_reader.wasm'),
        ]);
        prepareZXingModule({ overrides: { locateFile: (path, prefix) => (path.endsWith('.wasm') ? wasmUrl : prefix + path) } });
        stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: CAMERA_VIDEO });
        if (finished) { stream.getTracks().forEach((track) => track.stop()); return; }
        const video = videoRef.current;
        video.srcObject = stream;
        await video.play();
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', { willReadFrequently: true });
        const scan = async () => {
          if (finished) return;
          try {
            const { videoWidth: width, videoHeight: height } = video;
            if (width && height) {
              canvas.width = width;
              canvas.height = height;
              context.drawImage(video, 0, 0, width, height);
              const [hit] = await readBarcodes(context.getImageData(0, 0, width, height), {
                formats, tryHarder: true, maxNumberOfSymbols: 1,
              });
              if (finished) return;
              if (hit?.isValid && hit.text) {
                finish();
                handlers.current.onRead({ value: hit.text, format: hit.format });
                return;
              }
            }
            scanTimer = setTimeout(scan, SCAN_INTERVAL_MS);
          } catch (err) {
            fail(err);
          }
        };
        scan();
      } catch (err) {
        fail(err);
      }
    })();
    return finish;
  }, [formats]);

  return (
    <div role="dialog" aria-label="Camera barcode scanner" className="fixed inset-0 z-50 flex flex-col bg-black">
      <div className="relative flex-1 overflow-hidden">
        <video ref={videoRef} playsInline muted autoPlay className="h-full w-full object-cover" />
        <div className="pointer-events-none absolute inset-x-8 top-1/2 h-36 -translate-y-1/2 rounded-xl border-2 border-white/80" />
      </div>
      <div className="space-y-3 bg-black p-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-center text-white">
        <p className="text-sm">{slow
          ? 'Still looking. Hold the barcode flat inside the box, a little farther away, or cancel and type it.'
          : 'Point the camera at a location or product barcode.'}</p>
        <button type="button" onClick={onClose} className="h-11 w-full rounded-lg bg-white text-base font-medium text-gray-900">Cancel</button>
      </div>
    </div>
  );
};

export default CameraScanSheet;
